<template>
  <app-dialog
    v-model="open"
    size="md"
    :title="t('changelog.dialog.title')"
    :description="releaseDate"
    :close-label="t('changelog.dialog.dismiss')"
    @update:model-value="onModelValue"
  >
    <changelog-entry-list v-if="entries.length" :entries="entries" />

    <template #footer>
      <app-button variant="ghost" tone="neutral" size="sm" @click="goToChangelog">
        {{ t('changelog.dialog.see_all') }}
      </app-button>
      <app-button tone="primary" size="sm" @click="dismiss">
        {{ t('changelog.dialog.dismiss') }}
      </app-button>
    </template>
  </app-dialog>
</template>

<script setup>
  /**
   * One-time "what's new" dialog, shown after a release the operator marked
   * notable.
   *
   * The backend decides whether to show it (`show_dialog`): notable AND unseen
   * AND at least one entry this user is entitled to see. Deciding here would
   * mean shipping the version-comparison and entitlement rules to the client,
   * where they would drift.
   *
   * The seen-cursor lives on the user's profile, not in localStorage —
   * `clearAllPersistedState()` wipes `pf_*` keys on logout, so a local flag
   * would make this reappear after every sign-out.
   */
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppDialog from '@/components/ui/overlays/AppDialog.vue';
  import ChangelogEntryList from '@/features/changelog/components/ChangelogEntryList.vue';
  import { useChangelog } from '@/features/changelog/composables/useChangelog';
  import { useFeatureFlags } from '@/composables/useFeatureFlags';
  import { formatDate } from '@/i18n/formatters';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t, locale } = useI18n();
  const { showDialog, latestRelease, markChangelogSeen, isSidebarOpen, openChangelog } = useChangelog();
  const { isChangelogDialogEnabled } = useFeatureFlags();

  const open = ref(false);
  // Gated during the soft launch. The backend still decides IF there is
  // anything to show; the flag only decides whether we interrupt with it. The
  // What's new page stays reachable either way, so nobody loses the content.
  watch(
    [showDialog, isChangelogDialogEnabled, isSidebarOpen],
    ([shouldShow, enabled, sidebarOpen]) => {
      open.value = Boolean(shouldShow && enabled && !sidebarOpen);
    },
    { immediate: true }
  );

  // Only what a customer should read — `internal` entries never leave the
  // backend, but guard anyway so a future payload change cannot leak one.
  const entries = computed(() =>
    (latestRelease.value?.entries || []).filter((entry) => entry.audience !== 'internal')
  );

  // Shared formatter rather than a local Intl call — see i18n/formatters.js.
  const releaseDate = computed(() => {
    const raw = latestRelease.value?.released_at;
    return raw ? formatDate(raw, locale.value, { dateStyle: 'long' }) : '';
  });

  function dismiss() {
    open.value = false;
    markChangelogSeen();
  }

  function onModelValue(value) {
    // Closing by backdrop, Escape or the × counts as dismissal too — otherwise
    // the dialog returns on the next login for someone who clearly saw it.
    if (!value) dismiss();
  }

  function goToChangelog() {
    dismiss();
    openChangelog();
  }
</script>
